import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/api/client";
import { EmptyState } from "@/components/EmptyState";
import { TableSkeleton } from "@/components/Skeletons";
import { TaskQuickAdd } from "@/components/tasks/TaskQuickAdd";
import { TaskRow } from "@/components/tasks/TaskRow";
import { Tabs } from "@/components/Tabs";
import { DensityProvider } from "@/contexts/DensityContext";
import { CheckSquare, Search } from "lucide-react";
import { toast } from "sonner";

// Batch Item 6 (2026-02): internal Tasks, Mongo-only, staff-assignable — priority (High first)
// drives sort order everywhere. Status is 2-state (Open/Done), no "In Progress".
// Fast Tasks upgrade (2026-02): Quick Add + fully inline editing (no modals anywhere),
// optimistic complete with undo.
// Staff-only assignment + PM usability pass (2026-02): assignee is staff-only (founder's
// explicit instruction — freelancer/production_partner picker options removed); added
// project/assignee filters with the last-used view persisted in localStorage; list is now
// always sectioned Overdue -> Due Today -> Upcoming -> No Date (replaces the old
// mine-only-bucketed / all-grouped-by-assignee split, since the new assignee filter covers
// that narrowing need instead).
const VIEW_STORAGE_KEY = "njm_tasks_view";
const BUCKET_META = [
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Due Today" },
  { key: "upcoming", label: "Upcoming" },
  { key: "no_date", label: "No Date" },
];

function loadSavedView() {
  try {
    return JSON.parse(localStorage.getItem(VIEW_STORAGE_KEY) || "{}");
  } catch { return {}; }
}

export default function Tasks() {
  const saved = loadSavedView();
  const [scope, setScope] = useState(saved.scope || "mine"); // "mine" | "all"
  const [status, setStatus] = useState(saved.status || "Open"); // "Open" | "Done"
  const [projectFilter, setProjectFilter] = useState(saved.projectFilter || "");
  const [assigneeFilter, setAssigneeFilter] = useState(saved.assigneeFilter || "");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const listRef = useRef(null);

  const load = () => {
    setLoading(true);
    const params = { assignee: scope, status };
    if (projectFilter) params.project_id = projectFilter;
    if (assigneeFilter) params.assignee_id = assigneeFilter;
    api.get("/v1/tasks/", { params }).then((r) => { setItems(r.data.items || []); setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [scope, status, projectFilter, assigneeFilter]);
  useEffect(() => {
    api.get("/v1/tasks/assignable-principals").then((r) => setPrincipals(r.data.items || []));
    api.get("/v1/projects/picker").then((r) => setProjects(r.data.items || [])).catch(() => {});
  }, []);

  // PM usability pass (2026-02): persist last-used view across reloads.
  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ scope, status, projectFilter, assigneeFilter }));
  }, [scope, status, projectFilter, assigneeFilter]);

  // Keyboard: "/" focuses search from anywhere on the page, unless already typing somewhere.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => t.title.toLowerCase().includes(q) || (t.notes || "").toLowerCase().includes(q));
  }, [items, search]);

  // PM usability pass: always sectioned Overdue -> Due Today -> Upcoming -> No Date.
  const buckets = useMemo(() => {
    const overdue = filtered.filter((t) => t.date_bucket === "overdue");
    const today = filtered.filter((t) => t.date_bucket === "today");
    const upcoming = filtered.filter((t) => t.due_date && t.date_bucket !== "overdue" && t.date_bucket !== "today");
    const noDate = filtered.filter((t) => !t.due_date);
    return BUCKET_META.map((b) => ({ ...b, items: { overdue, today, upcoming, no_date: noDate }[b.key] })).filter((g) => g.items.length);
  }, [filtered]);

  const focusRow = (fromIndex, dir) => {
    const next = fromIndex + dir;
    rowRefs.current[next]?.focus();
  };

  // Phase 3 fix pass (2026-10, Cursor review): after Space completes a row (removing it from
  // the current view), focus moves to the next row, or the previous row if it was last, or the
  // list container if there was only one row — so arrow-key navigation can continue uninterrupted.
  const focusSiblingAfterComplete = (nextEl, prevEl) => {
    if (nextEl) { nextEl.focus(); return; }
    if (prevEl) { prevEl.focus(); return; }
    listRef.current?.focus();
  };

  const createTask = async (title) => {
    try {
      const r = await api.post("/v1/tasks/", { title, project_id: projectFilter || undefined });
      setItems((prev) => [r.data.task, ...prev]);
    } catch { toast.error("Couldn't create task"); }
  };

  const toggleStatus = async (task) => {
    const next = task.status === "Open" ? "Done" : "Open";
    const snapshot = items;
    setItems((prev) => prev.filter((t) => t.id !== task.id));
    try {
      await api.patch(`/v1/tasks/${task.id}`, { status: next });
      if (next === "Done") {
        toast.success("Task completed", { action: { label: "Undo", onClick: () => {
          setItems((prev) => [{ ...task, status: "Open", completed_at: null }, ...prev]);
          api.patch(`/v1/tasks/${task.id}`, { status: "Open" }).catch(() => {});
        } } });
      } else {
        toast.success("Task reopened");
      }
    } catch { toast.error("Couldn't update task"); setItems(snapshot); }
  };

  const patchTask = async (taskId, patch) => {
    const snapshot = items;
    setItems((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
    try {
      const r = await api.patch(`/v1/tasks/${taskId}`, patch);
      setItems((prev) => prev.map((t) => (t.id === taskId ? r.data.task : t)));
    } catch { toast.error("Couldn't save change"); setItems(snapshot); }
  };

  const removeTask = async (taskId) => {
    const snapshot = items;
    setItems((prev) => prev.filter((t) => t.id !== taskId));
    try { await api.delete(`/v1/tasks/${taskId}`); } catch { toast.error("Couldn't delete task"); setItems(snapshot); }
  };

  let rowIndex = -1;
  // Phase 3 (2026-10, visual reskin): Space (toggle complete) / Enter (open title edit) —
  // implemented at this wrapper level only, via the row's own existing public controls
  // (`onToggleStatus` already passed to TaskRow; the title edit is opened via a real click on
  // TaskRow's own title button, found by its stable `task-title-{id}` testid) — TaskRow.jsx
  // itself is NOT restructured/touched for this.
  // Phase 3 fix pass (2026-10, Cursor review): the original `e.target.tagName` guard was too
  // broad — it only excluded INPUT/TEXTAREA/SELECT, so pressing Enter while ANY plain <button>
  // inside the row (checkbox, delete, priority dot, due date, notes, assignee) or the project
  // <Link> had focus still bubbled up here, matched the Enter branch, and ALSO fired the title
  // click — on top of whatever that control's own handler/native click-on-Enter already did.
  // Now gated to act ONLY when the key event's target is the row's own focusable wrapper
  // (`e.currentTarget.firstElementChild` — TaskRow's root div, the only child of this wrapper)
  // — i.e. only while the row itself has focus, never a descendant control. Also ignores any
  // modifier key and key-repeat, and never calls preventDefault unless it actually acts.
  const Row = (t) => {
    rowIndex += 1;
    const idx = rowIndex;
    return (
      <div
        key={t.id}
        onKeyDown={(e) => {
          if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.repeat) return;
          if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== " " && e.key !== "Enter") return;
          if (e.target !== e.currentTarget.firstElementChild) return; // a descendant control — never hijack
          if (e.key === "ArrowDown") { e.preventDefault(); focusRow(idx, 1); return; }
          if (e.key === "ArrowUp") { e.preventDefault(); focusRow(idx, -1); return; }
          if (e.key === " ") {
            e.preventDefault();
            const nextEl = rowRefs.current[idx + 1] || null;
            const prevEl = rowRefs.current[idx - 1] || null;
            toggleStatus(t).then(() => focusSiblingAfterComplete(nextEl, prevEl));
            return;
          }
          e.preventDefault();
          e.currentTarget.querySelector(`[data-testid="task-title-${t.id}"]`)?.click();
        }}
      >
        <TaskRow
          task={t} principals={principals} showAssignee
          onToggleStatus={toggleStatus} onPatch={patchTask} onDelete={removeTask}
          rowRef={(el) => (rowRefs.current[idx] = el)}
        />
      </div>
    );
  };

  return (
    <DensityProvider density="compact" data-testid="tasks-page" className="p-8">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-editorial text-4xl tracking-tight flex items-center gap-2"><CheckSquare className="h-7 w-7 text-brand" /> Tasks</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-faint absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              ref={searchRef} data-testid="tasks-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search… ( / )" aria-label="Search tasks"
              className="rounded-md border border-hairline bg-paper pl-7 pr-2.5 py-1.5 text-xs text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
          </div>
          <select data-testid="tasks-filter-project" aria-label="Filter by project" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand/40">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select data-testid="tasks-filter-assignee" aria-label="Filter by assignee" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}
            className="rounded-md border border-hairline bg-paper px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand/40">
            <option value="">Everyone</option>
            {principals.map((p) => <option key={p.principal_id} value={p.principal_id}>{p.name}</option>)}
          </select>
          <Tabs variant="sub" testId="tasks-scope" value={scope} onChange={setScope}
            tabs={[{ key: "mine", label: "My Tasks" }, { key: "all", label: "Team Board" }]} />
          <Tabs variant="sub" testId="tasks-status" value={status.toLowerCase()} onChange={(k) => setStatus(k === "open" ? "Open" : "Done")}
            tabs={[{ key: "open", label: "Open" }, { key: "done", label: "Done" }]} />
        </div>
      </div>

      <div ref={listRef} tabIndex={-1} data-testid="tasks-list-container" className="rounded-md border border-hairline bg-paper overflow-hidden">
        <TaskQuickAdd onCreate={createTask} />
        {loading ? <TableSkeleton cols={3} testId="tasks-skeleton" /> : filtered.length === 0 ? (
          <EmptyState title="No tasks yet — type above to add one" testId="tasks-empty" />
        ) : (
          buckets.map((g) => (
            <div key={g.key}>
              <div className="bg-surface2 px-4 py-2 text-xs uppercase tracking-wider text-ink-soft border-b border-hairline" data-testid={`task-bucket-header-${g.key}`}>
                {g.label} ({g.items.length})
              </div>
              {g.items.map(Row)}
            </div>
          ))
        )}
      </div>
    </DensityProvider>
  );
}
